import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import Switch from '@mui/material/Switch'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  useMutationConfirmAddonSubscription,
  useMutationCreateAddonSubscription,
  useQueryGetAddons,
  useQueryGetCards
} from '../../../queries/paymentQueries'
import { useQueryGetUserMe } from '../../../queries/userQueries'
import { ToastContext } from '../../../context/ToastContext'
import SavedCardsList from '../views/dashboard/Ajustes/Pagos/SavedCardsList'
import AddonConfirmContent from '../views/dashboard/Ajustes/Pagos/AddonConfirmContent'
import { Modal } from './Modal'
import CustomButton from './CustomButton'
import ResizableModal from './ResizableModal'
import { refreshTryLuca } from '../../../utils/refreshTryLuca'

const IVA = 1.19

const ADDON_CONTRACT_SLIDES = {
  addons: 0,
  confirm: 1
}

const ADDON_CONTRACT_FLOW_STEPS = [
  { label: 'Contratar Addons', slide: ADDON_CONTRACT_SLIDES.addons },
  { label: 'Confirmar y validar Addons', slide: ADDON_CONTRACT_SLIDES.confirm }
]

const getPaymentErrorMessage = error => {
  if (!error) return 'No se pudo completar. Intenta de nuevo.'

  const data = error?.response?.data

  if (data != null && typeof data === 'object' && typeof data.message === 'string') {
    return data.message
  }

  if (typeof data === 'string') return data
  if (typeof error?.message === 'string') return error.message

  return 'No se pudo completar. Intenta de nuevo.'
}

const addonMatchesPermission = (addon, permission) => {
  if (!permission || !addon.affectedPermission) return false
  if (typeof addon.affectedPermission === 'string') return addon.affectedPermission === permission

  return Array.isArray(addon.affectedPermission) && addon.affectedPermission.includes(permission)
}

const buildInitialQuantities = (addonsList, preferredAddonPermission) => {
  const result = {}

  addonsList.forEach(addon => {
    const isPreselected = addonMatchesPermission(addon, preferredAddonPermission)

    result[addon.id] = {
      quantity: isPreselected ? 1 : 0,
      affectedPermission: addon.affectedPermission
    }
  })

  return result
}

const handleTryLucaAddonRefresh = (callback, onClose, options = {}) => {
  callback?.()
  onClose?.()
  refreshTryLuca(options)
}

const ContractAddonsModal = ({ isOpen, onClose, onSuccess, preferredAddonPermission }) => {
  const [showCardsModal, setShowCardsModal] = useState(false)
  const [quantities, setQuantities] = useState({})
  const [selectedCardId, setSelectedCardId] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [activeContractSlide, setActiveContractSlide] = useState(ADDON_CONTRACT_SLIDES.addons)
  const [paymentStep, setPaymentStep] = useState(1)
  const [toCreate, setToCreate] = useState({ addons: [], cardId: null })
  const [addonSubscriptionIdForConfirm, setAddonSubscriptionIdForConfirm] = useState(null)
  const [showAddonPaymentPendingReviewModal, setShowAddonPaymentPendingReviewModal] =
    useState(false)
  const [showAddonSuccessModal, setShowAddonSuccessModal] = useState(false)
  const contractSwiperRef = useRef(null)

  const { data: userMe } = useQueryGetUserMe()
  const organizationId = userMe?.currentOrganizationId || null
  const {
    data: { addons = [], ufValue = 0 } = { addons: [], ufValue: 0 },
    isLoading: addonsLoading
  } = useQueryGetAddons()
  const { data: cardsData, isLoading: cardsLoading } = useQueryGetCards()
  const cards = cardsData?.cards || []
  const customerId = cardsData?.customerId || null
  const cardsIds = (cards || [])
    .map(card => card.mercadopagoCardId)
    .filter(id => typeof id === 'string' && id.trim() !== '')
  const activeAddonCardId =
    selectedCardId || (toCreate?.cardId != null ? String(toCreate.cardId) : '')
  const selectedAddonPaymentCard = useMemo(() => {
    if (!activeAddonCardId) return null

    return cards.find(card => String(card.id) === activeAddonCardId) || null
  }, [activeAddonCardId, cards])
  const selectedAddonPaymentCardsIds = useMemo(() => {
    const mercadoPagoCardId = selectedAddonPaymentCard?.mercadopagoCardId

    if (typeof mercadoPagoCardId === 'string' && mercadoPagoCardId.trim() !== '') {
      return [mercadoPagoCardId.trim()]
    }

    return cardsIds
  }, [cardsIds, selectedAddonPaymentCard])
  const selectedAddonPaymentTypeId = selectedAddonPaymentCard?.paymentTypeId ?? null
  const createAddon = useMutationCreateAddonSubscription()
  const confirmAddon = useMutationConfirmAddonSubscription()
  const { showToast } = useContext(ToastContext)
  const prevOpenRef = useRef(false)

  const isPending = createAddon.isPending || confirmAddon.isPending

  const activeAddons = useMemo(
    () => addons.filter(addon => addon.isActive !== false),
    [addons]
  )

  const initialQuantities = useMemo(
    () => buildInitialQuantities(activeAddons, preferredAddonPermission),
    [activeAddons, preferredAddonPermission]
  )

  useEffect(() => {
    if (isOpen && !prevOpenRef.current && activeAddons.length > 0) {
      setQuantities(initialQuantities)
    }

    prevOpenRef.current = isOpen
  }, [activeAddons, initialQuantities, isOpen])

  const totalUf = useMemo(() => {
    const total = activeAddons.reduce((accumulator, addon) => {
      const entry = quantities[addon.id]
      const addonType = addon.type || 'number'
      const uf = Number(addon.ufPriceWithoutTaxes) || 0
      const quantity = Math.max(0, parseInt(entry?.quantity ?? 0, 10) || 0)

      if (addonType === 'bool') return accumulator + (quantity > 0 ? uf : 0)

      return accumulator + uf * quantity
    }, 0)

    return typeof total === 'number' && !Number.isNaN(total) ? total : 0
  }, [activeAddons, quantities])

  const totalClp = useMemo(() => {
    if (!ufValue || ufValue === 0) return 0

    return Math.round((totalUf || 0) * ufValue * IVA)
  }, [totalUf, ufValue])

  const selectedAddonsPreview = useMemo(() => {
    const addonsToCreate = []
    const summary = []

    activeAddons.forEach(addon => {
      const entry = quantities[addon.id]
      const quantity = Math.max(0, parseInt(entry?.quantity ?? 0, 10) || 0)

      if (quantity > 0) {
        addonsToCreate.push({ addonId: addon.id, quantity })
        summary.push({ addonId: addon.id, name: addon.name, quantity: `${quantity}` })
      }
    })

    return { addonsToCreate, summary }
  }, [activeAddons, quantities])

  const hasSelection = selectedAddonsPreview.addonsToCreate.length > 0
  const mainModalOpen =
    isOpen &&
    !showCardsModal &&
    !showAddonSuccessModal &&
    !showAddonPaymentPendingReviewModal

  const getContractSwiper = () => contractSwiperRef.current

  const updateContractSwiperLayout = () => {
    const swiper = getContractSwiper()

    if (!swiper || swiper.destroyed) return

    requestAnimationFrame(() => {
      if (!swiper || swiper.destroyed || !swiper.params) return

      swiper.updateAutoHeight?.(0)
      swiper.update?.()
    })
  }

  const goToContractSlide = slide => {
    const swiper = getContractSwiper()

    if (swiper && !swiper.destroyed) {
      swiper.slideTo(slide, 300)
    } else {
      setActiveContractSlide(slide)
    }

    requestAnimationFrame(updateContractSwiperLayout)
  }

  const resetContractState = nextQuantities => {
    setQuantities(nextQuantities)
    setSelectedCardId('')
    setErrorMessage('')
    setActiveContractSlide(ADDON_CONTRACT_SLIDES.addons)
    setPaymentStep(1)
    setToCreate({ addons: [], cardId: null })
    setAddonSubscriptionIdForConfirm(null)

    requestAnimationFrame(() => {
      const swiper = getContractSwiper()

      if (!swiper || swiper.destroyed) return

      swiper.slideTo(ADDON_CONTRACT_SLIDES.addons, 0)
      swiper.updateAutoHeight?.(0)
      swiper.update?.()
    })
  }

  useEffect(() => {
    if (!isOpen) return

    if (cards.length === 1) {
      const onlyCardId = String(cards[0].id)

      if (selectedCardId !== onlyCardId) {
        setSelectedCardId(onlyCardId)
      }

      return
    }

    if (selectedCardId && !cards.some(card => String(card.id) === String(selectedCardId))) {
      setSelectedCardId('')
    }
  }, [cards, isOpen, selectedCardId])

  const setQuantity = (addonId, value) => {
    const quantity = Math.max(0, parseInt(value, 10) || 0)

    setQuantities(prev => {
      const entry = prev[addonId] || { quantity: 0, affectedPermission: undefined }

      return {
        ...prev,
        [addonId]: {
          ...entry,
          quantity,
          affectedPermission: entry.affectedPermission
        }
      }
    })

    setErrorMessage('')
  }

  const setBooleanValue = (addonId, value) => {
    setQuantities(prev => {
      const entry = prev[addonId] || { quantity: 0, affectedPermission: undefined }

      return {
        ...prev,
        [addonId]: {
          ...entry,
          quantity: value ? 1 : 0,
          affectedPermission: entry.affectedPermission
        }
      }
    })

    setErrorMessage('')
  }

  const handleOpenAddCard = () => {
    setErrorMessage('')
    setShowCardsModal(true)
  }

  const handleSelectAddonCard = cardId => {
    setSelectedCardId(cardId)
    setErrorMessage('')
    setToCreate(prev => ({ ...prev, cardId: cardId ? Number(cardId) : null }))
  }

  const handleContinueToConfirm = () => {
    setErrorMessage('')

    if (!hasSelection) {
      setErrorMessage('Selecciona al menos un addon')
      return
    }

    const cardId = cards.length === 1 ? cards[0].id : selectedCardId || null
    const addonsToCreate = selectedAddonsPreview.addonsToCreate

    setToCreate({
      addons: addonsToCreate,
      cardId: cardId ? Number(cardId) : null
    })
    setPaymentStep(1)
    setAddonSubscriptionIdForConfirm(null)
    goToContractSlide(ADDON_CONTRACT_SLIDES.confirm)
  }

  const handlePaymentTokenSuccess = async ({ token, payerEmail }) => {
    if (!token || !organizationId) {
      showToast('error', 'No se pudo generar el token de pago')
      return
    }

    try {
      if (paymentStep === 1) {
        const { addons: addonsToCreate, cardId } = toCreate

        if (!addonsToCreate?.length || !cardId) {
          showToast('error', 'Datos de contratacion incompletos')
          return
        }

        const response = await createAddon.mutateAsync({
          organizationId,
          addons: addonsToCreate,
          cardId,
          token,
          payerEmail
        })

        const ids =
          response?.addonSubscriptionIds ??
          (response?.addonSubscriptionId != null ? [response.addonSubscriptionId] : [])
        const idForConfirm = ids[0] ?? response?.addonSubscriptions?.[0]?.id

        if (idForConfirm != null) {
          setAddonSubscriptionIdForConfirm(idForConfirm)
          setPaymentStep(2)
          setErrorMessage('')
        } else {
          resetContractState(initialQuantities)
          setShowAddonSuccessModal(true)
        }
      } else {
        const idForConfirm = addonSubscriptionIdForConfirm

        if (idForConfirm == null) {
          showToast('error', 'No se encontro suscripcion a confirmar')
          return
        }

        const confirmResult = await confirmAddon.mutateAsync({
          organizationId,
          addonSubscriptionId: idForConfirm,
          token
        })

        resetContractState(initialQuantities)

        if (confirmResult?.paymentPendingReviewManual) {
          setShowAddonPaymentPendingReviewModal(true)
        } else {
          setShowAddonSuccessModal(true)
        }
      }
    } catch (error) {
      const message = getPaymentErrorMessage(error)

      setErrorMessage(message)
      showToast('error', message)
      throw error
    }
  }

  const handleBackToAddonsSelection = () => {
    if (isPending) return

    setErrorMessage('')
    setPaymentStep(1)
    setAddonSubscriptionIdForConfirm(null)
    goToContractSlide(ADDON_CONTRACT_SLIDES.addons)
  }

  const handleClose = () => {
    if (isPending) return

    resetContractState({})
    onClose?.()
  }

  const handleCloseAddonPaymentPendingReviewModal = () => {
    setShowAddonPaymentPendingReviewModal(false)
    onClose?.()
  }

  const handleCloseAddonSuccessModal = () => {
    setShowAddonSuccessModal(false)
    handleTryLucaAddonRefresh(onSuccess, onClose)
  }

  const activeSlideMeta = {
    [ADDON_CONTRACT_SLIDES.addons]: {
      title: 'Contratar Addons',
      description: 'Selecciona los add-ons que quieres contratar o ajusta su cantidad.'
    },
    [ADDON_CONTRACT_SLIDES.confirm]: {
      title: 'Confirmar y validar Addons',
      description: 'Revisa el resumen y valida el cobro para completar la contratacion.'
    }
  }[activeContractSlide] ?? {
    title: 'Contratar add-ons',
    description: 'Selecciona los add-ons y confirma el cobro.'
  }
  const currentContractStep = activeContractSlide + 1
  const totalContractSteps = ADDON_CONTRACT_FLOW_STEPS.length

  const selectionFooter = (
    <>
      <CustomButton
        title='Cancelar'
        color='primary'
        className='rounded-full px-4 py-2'
        onClick={handleClose}
        disabled={isPending}
      />
      <CustomButton
        title='Continuar'
        color='accent2'
        className='rounded-full px-4 py-2'
        onClick={handleContinueToConfirm}
        disabled={!hasSelection || isPending}
      />
    </>
  )

  const confirmFooter = (
    <CustomButton
      title='Volver'
      color='primary'
      className='rounded-full px-4 py-2'
      onClick={handleBackToAddonsSelection}
      disabled={isPending}
    />
  )

  const modalFooterBySlide = {
    [ADDON_CONTRACT_SLIDES.addons]: selectionFooter,
    [ADDON_CONTRACT_SLIDES.confirm]: confirmFooter
  }

  const selectionContent = (
    <div className='space-y-5 py-3'>
      {activeAddons.length === 0 ? (
        <div className='rounded-[24px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center shadow-sm'>
          <h5 className='text-lg font-bold text-slate-800'>No hay add-ons disponibles</h5>
          <p className='mt-3 text-sm leading-6 text-slate-600'>
            Cuando existan add-ons activos para tu organizacion, apareceran aqui para que los
            puedas contratar.
          </p>
        </div>
      ) : (
        <div className='grid gap-3 lg:grid-cols-2'>
          {activeAddons.map(addon => {
            const addonType = addon.type || 'number'
            const isBoolean = addonType === 'bool'
            const quantity = Math.max(0, parseInt(quantities[addon.id]?.quantity ?? 0, 10) || 0)
            const isSelectedAddon = quantity > 0
            const cardTone = isSelectedAddon
              ? {
                  shell:
                    'border-accent2 bg-gradient-to-b from-accent2/10 via-white to-white shadow-lg',
                  topBar: 'bg-gradient-to-r from-accent2 via-primary to-accent2',
                  pricePanel: 'border-accent2/15 bg-white/95'
                }
              : {
                  shell:
                    'border-gray-200 bg-gradient-to-b from-white via-white to-gray-50/70 shadow-sm hover:-translate-y-1 hover:border-accent2/30 hover:shadow-xl',
                  topBar: 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200',
                  pricePanel: 'border-gray-200 bg-white/90'
                }

            return (
              <div
                key={addon.id}
                className={`group relative flex h-full flex-col overflow-hidden rounded-[24px] border transition-all duration-200 ${cardTone.shell}`}
              >
                <div className={`absolute inset-x-0 top-0 h-1 ${cardTone.topBar}`} />

                <div className='flex h-full flex-col p-5 sm:p-6'>
                  <div className='flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between'>
                    <div className='min-w-0 flex-1'>
                      <div className='flex flex-wrap items-center gap-2'>
                        <h5 className='text-xl font-sans font-semibold tracking-tight text-gray-900 sm:text-[1.35rem]'>
                          {addon.name}
                        </h5>
                        <span className='inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[10px] font-sans font-semibold uppercase tracking-[0.14em] text-gray-500'>
                          {isBoolean ? 'Activacion' : 'Cantidad variable'}
                        </span>
                      </div>

                      {addon.description && (
                        <p className='mt-1.5 text-[13px] font-sans leading-5 text-gray-600 sm:text-sm'>
                          {addon.description}
                        </p>
                      )}
                    </div>

                    <div className='flex shrink-0 items-center gap-2.5'>
                      {!isBoolean && (
                        <label className='text-[13px] font-sans font-medium text-slate-600'>
                          Cantidad
                        </label>
                      )}

                      {isBoolean ? (
                        <Switch
                          checked={isSelectedAddon}
                          onChange={event => setBooleanValue(addon.id, event.target.checked)}
                          color='primary'
                        />
                      ) : (
                        <input
                          type='number'
                          min={0}
                          value={quantity}
                          onChange={event => setQuantity(addon.id, event.target.value)}
                          className='w-20 rounded-full border border-slate-300 px-3 py-1.5 text-center text-sm font-semibold text-slate-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary/30'
                        />
                      )}
                    </div>
                  </div>

                  <div className={`mt-4 rounded-[20px] border p-4 ${cardTone.pricePanel}`}>
                    <div>
                      <p className='text-[10px] font-sans font-semibold uppercase tracking-[0.16em] text-gray-500'>
                        Precio
                      </p>
                      <div className='mt-1.5 flex flex-wrap items-end gap-2'>
                        <span className='text-[1.875rem] font-bold leading-none text-gray-900 sm:text-[2rem]'>
                          {addon.ufPriceWithoutTaxes} UF
                        </span>
                        <span className='pb-0.5 text-[13px] font-sans font-medium text-gray-500 sm:text-sm'>
                          + IVA {isBoolean ? '' : 'c/u'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div className='rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm'>
        <div className='flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between'>
          <div>
            <p className='text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/80'>
              Resumen
            </p>
            <h4 className='mt-2 text-xl font-bold text-slate-800'>Total estimado del cobro</h4>
            <p className='mt-2 text-sm leading-6 text-slate-600'>
              El monto final se cobrara en la tarjeta guardada de manera independiente al plan contratado.
            </p>
          </div>

          <div className='rounded-[20px] border border-accent2 bg-accent2/5 px-4 py-4 lg:min-w-[280px]'>
            <p className='text-xl font-sans font-semibold text-slate-600'>
              Total:{' '}
              <span className='font-semibold text-slate-800'>
                {(Number(totalUf) || 0).toFixed(2)} UF + IVA
              </span>
            </p>
          </div>
        </div>
      </div>

      {errorMessage && activeContractSlide === ADDON_CONTRACT_SLIDES.addons && (
        <div className='rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700'>
          {errorMessage}
        </div>
      )}
    </div>
  )

  const confirmContent = (
    <AddonConfirmContent
      selectedAddonsSummary={selectedAddonsPreview.summary}
      totalUf={totalUf}
      totalClp={totalClp}
      ufValue={ufValue}
      paymentStep={paymentStep}
      onPaymentSuccess={handlePaymentTokenSuccess}
      isPending={isPending}
      customerId={customerId}
      cardsIds={selectedAddonPaymentCardsIds}
      selectedCard={selectedAddonPaymentCard}
      cards={cards}
      cardsLoading={cardsLoading}
      selectedCardId={selectedCardId}
      onSelectCard={handleSelectAddonCard}
      onOpenAddCard={handleOpenAddCard}
      errorMessage={errorMessage}
      paymentTypeId={selectedAddonPaymentTypeId}
    />
  )

  if (addonsLoading) {
    return (
      <ResizableModal
        open={isOpen}
        onClose={handleClose}
        size='compact'
        width={720}
        height={320}
        minWidth={520}
        minHeight={260}
        borderRadius='1.75rem'
      >
        <div className='flex h-full items-center justify-center py-12'>
          <div className='h-12 w-12 animate-spin rounded-full border-b-4 border-t-4 border-accent1' />
        </div>
      </ResizableModal>
    )
  }

  if (isOpen && userMe?.plan?.isTrial === true) {
    return (
      <ResizableModal
        open={isOpen}
        onClose={handleClose}
        size='compact'
        width={560}
        height={340}
        minWidth={520}
        minHeight={300}
        borderRadius='1.75rem'
        footer={
          <CustomButton
            title='Entendido'
            color='primary'
            className='rounded-full px-4 py-2'
            onClick={handleClose}
          />
        }
      >
        <div className='flex min-h-0 flex-col'>
          <div className='border-b border-gray-200 pb-4'>
            <h3 className='text-2xl font-bold text-gray-800'>Add-ons no disponibles</h3>
            <p className='mt-1 text-sm text-gray-600'>
              Los add-ons no estan disponibles durante el periodo de prueba.
            </p>
          </div>
          <p className='pt-4 text-sm leading-6 text-gray-600'>
            Contrata un plan para poder habilitar la compra de add-ons en tu organizacion.
          </p>
        </div>
      </ResizableModal>
    )
  }

  return (
    <>
      <ResizableModal
        open={mainModalOpen}
        onClose={handleClose}
        size='large'
        width={1240}
        height={860}
        minWidth={960}
        minHeight={640}
        borderRadius='1.75rem'
        footer={modalFooterBySlide[activeContractSlide]}
      >
        <div className='flex h-full min-h-0 flex-col'>
          <div className='border-b border-gray-200 pb-4'>
            <div className='flex flex-col items-center gap-3 text-center'>
              <div className='w-full max-w-3xl'>
                <div className='flex w-full items-center justify-center rounded-full border border-primary/10 bg-primary/5 px-4 py-2 transition-colors duration-200'>
                  <span className='text-sm font-sans font-semibold text-center text-primary sm:text-base'>
                    Paso {currentContractStep} de {totalContractSteps} - {activeSlideMeta.title}
                  </span>
                </div>
              </div>

              <div className='max-w-2xl'>
                <p className='mt-1 font-sans text-sm text-gray-600'>{activeSlideMeta.description}</p>
              </div>
            </div>
          </div>

          <div className='flex-1 min-h-0 pt-4'>
            <Swiper
              allowTouchMove={false}
              observer
              observeParents
              onSwiper={swiper => {
                contractSwiperRef.current = swiper
                updateContractSwiperLayout()
              }}
              onSlideChange={swiper => {
                setActiveContractSlide(swiper.activeIndex)
                setErrorMessage('')
                requestAnimationFrame(() => {
                  if (!swiper || swiper.destroyed || !swiper.params) return

                  swiper.updateAutoHeight?.(0)
                  swiper.update?.()
                })
              }}
              className='h-full min-h-0'
            >
              <SwiperSlide className='h-full'>
                <div className='h-full overflow-y-auto pr-1'>{selectionContent}</div>
              </SwiperSlide>

              <SwiperSlide className='h-full'>
                <div className='h-full overflow-y-auto pr-1'>{confirmContent}</div>
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </ResizableModal>

      <Modal isOpen={showCardsModal} onClose={() => setShowCardsModal(false)}>
        <div className='flex max-h-[80vh] w-[min(48rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl bg-white shadow-2xl'>
          <div className='flex items-center justify-end bg-primary px-4 py-2 text-white sm:px-8'>
            <button
              type='button'
              onClick={() => setShowCardsModal(false)}
              className='rounded-full p-2 text-white transition-colors hover:bg-white/15'
              aria-label='Cerrar modal'
            >
              <ClearIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          <div className='flex-1 overflow-y-auto px-6 py-8 sm:px-8'>
            <div className='mb-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-sm'>
              <h2 className='text-2xl font-sans font-bold text-slate-800'>Agregar tarjeta</h2>
              <p className='mt-3 text-sm font-sans leading-6 text-slate-700'>
                Para contratar addons necesitas al menos una tarjeta guardada.
              </p>
            </div>

            <SavedCardsList />
          </div>

          <div className='flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8'>
            <CustomButton
              type='button'
              title='Volver'
              onClick={() => setShowCardsModal(false)}
              color='primary'
              className='rounded-full px-5 py-2'
            />
          </div>
        </div>
      </Modal>

      <Modal isOpen={showAddonSuccessModal} onClose={handleCloseAddonSuccessModal}>
        <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
          <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-green-100'>
            <svg
              className='h-8 w-8 text-green-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M5 13l4 4L19 7'
              />
            </svg>
          </div>
          <h3 className='mb-2 text-xl font-bold text-gray-900'>Addons contratados correctamente</h3>
          <p className='mb-6 text-sm text-gray-600'>
            Tu contratacion de addons se ha realizado con exito.
          </p>
          <CustomButton
            title='Entendido'
            color='primary'
            className='rounded-full px-6 py-2'
            onClick={handleCloseAddonSuccessModal}
          />
        </div>
      </Modal>

      <Modal
        isOpen={showAddonPaymentPendingReviewModal}
        onClose={handleCloseAddonPaymentPendingReviewModal}
      >
        <div className='w-full max-w-md rounded-lg bg-white p-8 text-center shadow-xl'>
          <div className='mx-auto mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-amber-100'>
            <svg
              className='h-8 w-8 text-amber-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          </div>
          <h3 className='mb-2 text-xl font-bold text-gray-900'>Pago en revision</h3>
          <p className='mb-6 text-sm text-gray-600'>
            Tu pago esta en revision. Te avisaremos cuando sea confirmado.
          </p>
          <CustomButton
            title='Entendido'
            color='primary'
            className='rounded-full px-6 py-2'
            onClick={handleCloseAddonPaymentPendingReviewModal}
          />
        </div>
      </Modal>
    </>
  )
}

export default ContractAddonsModal
